import { startsWith } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import DataTable from '../../../core/components/DataTable';
import TableActionButtonsCell from '../../../core/components/TableActionButtonsCell';
import FilterSelector from '../../../core/components/FilterSelector';
import TaskTypeFilter from '../../tasks/containers/TaskTypeFilter';
import { selectEmployees } from '../../employees/reducer';
import { fetchEmployees } from '../../employees/actions';
import { selectTaskLocations, selectTaskPrefixes } from '../../tasks/reducer';
import { getTaskLocations, getTaskPrefixes } from '../../tasks/actions';
import { selectSiteOperationTypes } from '../../settings/reducer';
import { getSiteOperationTypes } from '../../settings/actions';
import { apiError } from '../../../core/toastMessages';
import { TASK_DRAWER_WIDTH } from '../../tasks/types';
import {
  TaskTemplate,
  useGetTaskTemplateQuery,
  useGetTaskTemplatesQuery,
  useSetOccurrenceStatusByIdMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import {
  UserGroupPermissions,
  addPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { useOptimisticList } from '../../../core/hooks/useOptimisticList';
import { getTaskTemplateIntervalDescription, getTaskTemplateLastCompleted } from '../helpers';
import { TaskDetails } from '../components/TaskDetails';
import { TaskType } from '../../tasks/models';
import { Drawer } from '../../../core/components/Drawer';

// TasksV2Admin owns the list (DataTable, filters, pagination, optimistic mirror).
// The add/edit drawer lives in TaskTemplateAdminDrawer so the same UI can be
// reused from the Assets page ("Create new task" under an asset).

const TasksV2Admin = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const employees = useSelector(selectEmployees);
  const locations = useSelector(selectTaskLocations) as any[];
  const operationTypes = useSelector(selectSiteOperationTypes) as any[];
  const taskPrefixes = useSelector(selectTaskPrefixes) as Array<{ type: number; message: string }>;

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [highlightedRow, setHighlightedRow] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [isNewTask, setIsNewTask] = useState<boolean>(false);
  const [newTaskType, setNewTaskType] = useState<number | null>(null);

  const pageSize = 25;

  const queryParams = useMemo(() => {
    const params: Record<string, any> = { page, pageSize };
    if (typeFilter) params.type = typeFilter;
    return params;
  }, [page, typeFilter]);

  const { data, isLoading, isFetching, refetch } = useGetTaskTemplatesQuery(queryParams);
  const [setOccurrenceStatus] = useSetOccurrenceStatusByIdMutation();
  const { data: fetchedTemplate } = useGetTaskTemplateQuery(selectedTemplate?.id as number, {
    skip: !selectedTemplate?.id,
  });

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (!locations.length) dispatch(getTaskLocations());
  }, [dispatch, locations.length]);

  useEffect(() => {
    if (!operationTypes.length) dispatch(getSiteOperationTypes());
  }, [dispatch, operationTypes.length]);

  useEffect(() => {
    if (!taskPrefixes.length) dispatch(getTaskPrefixes());
  }, [dispatch, taskPrefixes.length]);

  // Deep-link `?addType=Maintenance` opens the drawer in add-mode preseeded to that type.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const addType = params.get('addType');
    if (!addType) return;
    const typeNum = (TaskType as Record<string, number>)[addType] ?? null;
    if (typeNum !== null) {
      setIsNewTask(true);
      setSelectedTemplate(null);
      setNewTaskType(typeNum);
      setDrawerOpen(true);
    }
    params.delete('addType');
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Local mirror lets drawer save / archive patch the table optimistically.
  const {
    localList: localTasks,
    updateItem: updateLocalTask,
    restoreItem: restoreLocalTask,
    listRef: localTasksRef,
  } = useOptimisticList(data?.results || [], {
    compareFields: [
      'name',
      'subTitle',
      'description',
      'type',
      'interval',
      'intervalCount',
      'start',
      'end',
      'priority',
      'employeeId',
      'locationId',
      'operationTypeId',
      'estimatedTime',
      'capacity',
      'resource',
      'archivedAt',
      'latestOccurrence',
    ],
  });

  const visibleTasks: TaskTemplate[] = useMemo(
    () => localTasks.filter((t) => t.type !== 'Project' && !(t as any).archivedAt),
    [localTasks]
  );

  const handleAddClick = () => {
    setIsNewTask(true);
    setSelectedTemplate(null);
    setNewTaskType(null);
    setDrawerOpen(true);
  };

  const handleRowClick = (rowData: any) => {
    const id = rowData._id;
    const task = (data?.results || []).find((t) => t.id === id);
    if (!task) return;
    setIsNewTask(false);
    setSelectedTemplate(task);
    setDrawerOpen(true);
    setHighlightedRow(id);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTemplate(null);
    setIsNewTask(false);
    setNewTaskType(null);
  };

  // Inline "Done" mirrors legacy Tasks.js handleDone.
  const handleDone = async (_event: any, { _id }: { _id: number }) => {
    const task = (data?.results || []).find((t) => t.id === _id);
    if (!task || !task.latestOccurrence) {
      apiError({
        name: 'No occurrence',
        message: 'No scheduled occurrence yet. Run the scheduler before marking done.',
      });
      return;
    }
    try {
      await setOccurrenceStatus({
        occurrenceId: task.latestOccurrence.id,
        status: 'Completed',
      }).unwrap();
      refetch();
    } catch (e: any) {
      apiError(e);
    }
  };

  const dataRows = visibleTasks.map((task) => ({
    _id: task.id,
    _taskId: task.id,
    _showActions: true,
    name: task.name,
    _subTitle: task.subTitle,
    intervalDescription: getTaskTemplateIntervalDescription(task, intl),
    start: task.start ? moment(task.start).format('MM/DD/YYYY') : '',
    end: task.end ? moment(task.end).format('MM/DD/YYYY') : '',
    lastCompleted: getTaskTemplateLastCompleted(task.latestOccurrence, employees),
    _resource: task.resource || null,
  }));

  const headers = [
    { name: intl.formatMessage({ id: 'name' }), databaseProperty: 'name' },
    { name: intl.formatMessage({ id: 'interval' }), databaseProperty: 'intervalDescription' },
    { name: intl.formatMessage({ id: 'startDate' }), databaseProperty: 'start' },
    { name: intl.formatMessage({ id: 'endDate' }), databaseProperty: 'end' },
    { name: intl.formatMessage({ id: 'lastCompleted' }) },
    {
      name: intl.formatMessage({ id: 'resource' }),
      cell: {
        component: (props: any) => {
          const url = startsWith(props.rowData._resource, 'http')
            ? props.rowData._resource
            : `https://${props.rowData._resource}`;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {props.rowData._resource && (
                <i className="icon icon-link px-4 text-info" title={`${props.rowData._resource}`} />
              )}
            </a>
          );
        },
      },
    },
    {
      name: intl.formatMessage({ id: 'action' }),
      cell: {
        component: TableActionButtonsCell,
        mixIns: {
          buttons: [
            {
              title: 'Done',
              class: 'button green small',
              buttonAction: handleDone,
            },
          ],
        },
      },
    },
  ];

  const paginationInfo = {
    total: data?.total || 0,
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    goToPage: (next: number) => setPage(next),
  };

  return (
    <Container fluid className="no-padding">
      <Row className="todays-tasks">
        <Col>
          <div className="filters-actions-container">
            <FilterSelector
              filters={[
                {
                  component: TaskTypeFilter,
                  props: {
                    selectedFilter: typeFilter,
                    setFilter: (f: string) => {
                      setTypeFilter(f);
                      setPage(0);
                    },
                  },
                },
              ]}
              disabled={false}
              isLoading={false}
            />
            <UserGroupPermissions itemRef={addPermissionRefs.task}>
              <button className="button" onClick={handleAddClick} type="button">
                {intl.formatMessage({ id: 'addTask' })}
              </button>
            </UserGroupPermissions>
          </div>
          <div className="page-action-buttons" />
        </Col>
      </Row>
      <div>
        <DataTable
          showLoader={isLoading || isFetching}
          paginationInfo={paginationInfo}
          highlightedRow={highlightedRow}
          scrolling
          data={dataRows}
          headers={headers}
          rowClickInfo={{ callback: handleRowClick }}
        />
      </div>
      <Drawer
        expanded={drawerOpen}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseDrawer}
      >
        {drawerOpen && (
          <TaskDetails
            template={isNewTask ? null : fetchedTemplate ?? null}
            isNew={isNewTask}
            type={isNewTask ? newTaskType : undefined}
            onClose={handleCloseDrawer}
            onSave={handleCloseDrawer}
          />
        )}
      </Drawer>
    </Container>
  );
};

export default TasksV2Admin;
